import {
    COUPONS_TABLE,
    GOOGLE_SESSIONS_TABLE,
    MEDIATOR_ADDITIONAL_INFO_TABLE,
    MEDIATOR_RELATIONSHIPS_TABLE,
    MEDIATOR_SETTINGS_TABLE,
    MEDIATOR_UPLOAD_FILES_TABLE,
    MEDIATORS_TABLE,
    OUTLOOK_SESSIONS_TABLE,
    STRIPE_SESSIONS_TABLE,
    ZOOM_TABLE,
} from 'constants/db.contants'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { supabase } from 'utils/supabase'
import ApiService from './ApiService'
import { processMediatorAccountData } from 'utils/helper/processMediatorAccountData'

dayjs.extend(utc)
dayjs.extend(timezone)

// Reminder schedules JSONB helpers
// Categories map to top-level keys inside mediator_settings.reminder_schedules
export const REMINDER_SCHEDULE_CATEGORIES = {
    ONBOARDING: 'onboarding',
    BRIEF: 'brief',
    PAYMENT: 'payment',
}

// Field pattern inside each category object: reminder_<n>_days
export const REMINDER_SCHEDULE_FIELD_REGEX = /^reminder_(\d+)_days$/

export const getMediatorInformation = async (id) => {
    const { data, error } = await supabase
        .from(MEDIATORS_TABLE)
        .select('*')
        .eq('mediator_id', id)
        .single()

    if (error) throw error
    return data
}

export const UpdateMediatorAccount = async (mediatorData, mediatorId) => {
    const { data, error: mediatorError } = await supabase
        .from(MEDIATORS_TABLE)
        .update(mediatorData)
        .eq('user_id', mediatorId)
        .select()
        .single()

    if (mediatorError) {
        console.error('Error updating mediator:', mediatorError)
        return false
    }

    return data
}

export const UpdateMediatorAccountSettings = async (
    mediatorData,
    mediatorId
) => {
    const { data, error: mediatorError } = await supabase
        .from(MEDIATOR_SETTINGS_TABLE)
        .update(mediatorData)
        .eq('mediator_id', mediatorId)
        .select()
        .single()

    if (mediatorError) {
        console.error('Error updating settings for mediator:', mediatorError)
        return false
    }

    return data
}

const createDateFromTimeString = (timeString) => {
    if (!timeString) return null
    const [hours, minutes, seconds] = timeString.split(':').map(Number)
    const now = new Date()
    now.setHours(hours, minutes, seconds, 0)
    return now
}

// Helper function to parse JSON string or return array
const parseJsonOrArray = (value) => {
    if (!value) return []
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch {
            return []
        }
    }
    return Array.isArray(value) ? value : []
}

export const findIdofMediator = async (mediatorId) => {
    const { data, error } = await supabase
        .from(MEDIATORS_TABLE)
        .select('*')
        .eq('user_id', mediatorId)
        .single()

    if (error) {
        console.error('Error fetching mediator ID:', error)
    } else {
        return data.mediator_id
    }
}

export const getAccountData = async (mediatorId) => {
    const { data, error } = await supabase
        .from(MEDIATORS_TABLE)
        .select(
            `mediator_id, 
        first_name, 
        last_name, 
        email, 
        phone,
        photo_url,
        half_day_rate, 
        full_day_rate, 
        processing_fee_to_client, 
        hourly_rate,
        car_hourly_rate,
        deposit,
        pricing_type,
        cancellation_policy, 
        additional_information,        
        invoice_footer, 
        payment_reminder_days, 
        brief_reminder_days, 
        morning_time, 
        afternoon_time, 
        fullday_time, 
        timezone, 
        user_id, 
        status, 
        created_at, 
        fee_amount,
        fee_type,
        availability,
        bio,
        email_cc,
        email_cc_mediators,
        is_odr_mediator,
        custom_percentage_for_coupon,
        admin_fee,
        fee_amount,
        fee_type,
        is_corporate_code_visible,
        is_additional_case_names_allow,
        invoice_allocation_party,
        proceed_conflict_allowed,
        is_car_portal,
        ${STRIPE_SESSIONS_TABLE}(uuid,user_id,mediator_id,stripe_id,created_at,stripe_logo,stripe_address, business_name, business_address, business_phone_no, business_email, business_website),
        ${ZOOM_TABLE}(id,mediator_id,created_at,token,zoom_name,zoom_email),
        ${GOOGLE_SESSIONS_TABLE}(id,uuid,user_id, mediator_id, user_email, created_at),
        ${OUTLOOK_SESSIONS_TABLE}(id,uuid,user_id, mediator_id, user_email, created_at),
        ${COUPONS_TABLE}(*),
        ${MEDIATOR_SETTINGS_TABLE}(*),
        ${MEDIATOR_UPLOAD_FILES_TABLE}(*),
        ${MEDIATOR_ADDITIONAL_INFO_TABLE}(*),
        ${MEDIATOR_RELATIONSHIPS_TABLE}!mediator_relationships_parent_mediator_id_fkey(*,child_mediator_id:mediator_relationships_child_mediator_id_fkey(first_name,last_name,email,mediator_id,user_id))`
        )
        .order('created_at', {
            foreignTable: GOOGLE_SESSIONS_TABLE,
            ascending: false,
        })
        .eq('user_id', mediatorId)
        .eq(`${GOOGLE_SESSIONS_TABLE}.is_expired`, false)
        .eq(`${OUTLOOK_SESSIONS_TABLE}.is_expired`, false)

    if (error) {
        console.error('Error fetching account data:', error)
        return null
    }

    const accountData = data?.[0]

    if (accountData) {
        return processMediatorAccountData(accountData)
    }

    return {}
}

export const updateMediatorAdditionalInfo = async (
    mediatorId,
    additionalInfo
) => {
    try {
        // Check if record exists
        const { data: existingData, error: fetchError } = await supabase
            .from(MEDIATOR_ADDITIONAL_INFO_TABLE)
            .select('*')
            .eq('mediator_id', mediatorId)
            .maybeSingle()

        if (fetchError && fetchError.code !== 'PGRST116') {
            // PGRST116 is "not found" which is fine for first insert
            console.error('Error fetching additional info:', fetchError)
            // throw fetchError
        }

        const payload = {
            mediator_id: mediatorId,
            practice_areas: additionalInfo.practice_areas
                ? JSON.stringify(additionalInfo.practice_areas)
                : null,
            languages: additionalInfo.languages
                ? JSON.stringify(additionalInfo.languages)
                : null,
            education: additionalInfo.education
                ? JSON.stringify(additionalInfo.education)
                : null,
            bar_admissions: additionalInfo.bar_admissions
                ? JSON.stringify(additionalInfo.bar_admissions)
                : null,
            awards: additionalInfo.awards
                ? JSON.stringify(additionalInfo.awards)
                : null,
            service_area: additionalInfo.service_area || null,
        }

        let result
        if (existingData) {
            // Update existing record
            const { data, error } = await supabase
                .from(MEDIATOR_ADDITIONAL_INFO_TABLE)
                .update(payload)
                .eq('mediator_id', mediatorId)
                .select()
                .single()

            if (error) {
                console.error('Error updating additional info:', error)
                throw error
            }
            result = data
        } else {
            // Insert new record
            const { data, error } = await supabase
                .from(MEDIATOR_ADDITIONAL_INFO_TABLE)
                .insert(payload)
                .select()
                .single()

            if (error) {
                console.error('Error inserting additional info:', error)
                throw error
            }
            result = data
        }

        return result
    } catch (error) {
        console.error('Error in updateMediatorAdditionalInfo:', error)
        throw error
    }
}

export const getMediatorDataByUserId = async (user_id) => {
    try {
        const response = await ApiService.fetchData({
            url: `/mediator/by-id/${user_id}`,
            method: 'get',
        })
        return response?.data?.mediatorData
    } catch (error) {
        throw error
    }
}

export const getMediatorCouponsByMediatorId = async (
    mediator_id,
    is_odr_mediator
) => {
    try {
        let query = supabase
            .from(COUPONS_TABLE)
            .select('*')
            .eq('mediator_id', mediator_id)
            .eq('is_deleted', false)

        const { data, error } = await query

        if (error) {
            throw error
        }
        return data
    } catch (err) {
        throw err
    }
}

const fetchMediatorFilesByType = async (mediator_id, file_type) => {
    try {
        const { data, error } = await supabase
            .from(MEDIATOR_UPLOAD_FILES_TABLE)
            .select('*')
            .eq('mediator_id', mediator_id)
            .eq('file_type', file_type)
            .order('created_at', { ascending: false })

        if (error) {
            throw error
        }

        return data || []
    } catch (error) {
        throw error
    }
}

const uploadMediatorFilesByType = async (mediator_id, files, endpoint) => {
    try {
        const formData = new FormData()
        files.forEach((file) => formData.append('files', file))
        formData.append('mediator_id', mediator_id)

        const response = await ApiService.uploadFormData({
            url: endpoint,
            method: 'post',
            data: formData,
        })

        return response?.data
    } catch (error) {
        throw error
    }
}

const deleteMediatorFileRecord = async (fileId, mediator_id) => {
    try {
        const { error } = await supabase
            .from(MEDIATOR_UPLOAD_FILES_TABLE)
            .delete()
            .eq('id', fileId)
            .eq('mediator_id', mediator_id)

        if (error) {
            throw error
        }

        return true
    } catch (error) {
        throw error
    }
}

export const getMediatorAgreementFiles = async (mediator_id) =>
    fetchMediatorFilesByType(mediator_id, 'agreement_form')

export const getMediatorW9Files = async (mediator_id) =>
    fetchMediatorFilesByType(mediator_id, 'w9_form')

export const uploadMediationAgreementFiles = async (mediator_id, files) =>
    uploadMediatorFilesByType(
        mediator_id,
        files,
        '/upload/mediation-agreement-file'
    )

export const uploadMediatorW9Files = async (mediator_id, files) =>
    uploadMediatorFilesByType(mediator_id, files, '/upload/mediator-w9-file')

export const deleteMediatorAgreementFile = async (fileId, mediator_id) =>
    deleteMediatorFileRecord(fileId, mediator_id)

export const deleteMediatorW9File = async (fileId, mediator_id) =>
    deleteMediatorFileRecord(fileId, mediator_id)

// ---------------------------------------------------------------------------
// Case Manager Notes (READ-ONLY)
// ---------------------------------------------------------------------------
// These helpers let the mediator's calendar surface notes that a linked case
// manager left on specific (date, period) slots. The mediator can only VIEW
// these notes — never add, edit, or delete them. The case manager portal owns
// all writes to `cm_schedule_slots`.
// ---------------------------------------------------------------------------

/**
 * Returns true when the logged-in mediator has at least one row in
 * `case_manager_mediators`. Uses a HEAD count so no row payload is shipped.
 *
 * @param {number} mediatorIdInt mediators.mediator_id (integer PK)
 * @returns {Promise<boolean>}
 */
export const apiHasAssignedCaseManager = async (mediatorIdInt) => {
    if (!mediatorIdInt && mediatorIdInt !== 0) return false
    const { count, error } = await supabase
        .from('case_manager_mediators')
        .select('id', { count: 'exact', head: true })
        .eq('mediator_id', Number(mediatorIdInt))

    if (error) {
        console.warn('apiHasAssignedCaseManager: query failed', error)
        return false
    }
    return (count || 0) > 0
}

/**
 * Fetches case-manager notes for every slot the mediator owns inside the
 * given inclusive date range. Returns a lookup keyed by `${date}-${period}`
 * where period is 'AM' or 'PM'. Each value is an array of
 * `{ text, author, timestamp }` objects.
 *
 * Notes are stored as a JSON-encoded text array in `cm_schedule_slots.notes`.
 * Malformed JSON falls back to an empty list and a console warning.
 *
 * @param {string} mediatorUuid mediators.user_id (uuid) — matches
 *   cm_schedule_slots.mediator_id
 * @param {string} startOfMonthIso YYYY-MM-DD
 * @param {string} endOfMonthIso   YYYY-MM-DD
 * @returns {Promise<Record<string, Array<{text:string,author:string,timestamp:string}>>>}
 */
export const apiGetCaseManagerNotes = async (
    mediatorUuid,
    startOfMonthIso,
    endOfMonthIso
) => {
    const empty = {}
    if (!mediatorUuid || !startOfMonthIso || !endOfMonthIso) return empty

    const { data, error } = await supabase
        .from('cm_schedule_slots')
        .select('date, period, notes')
        .eq('mediator_id', mediatorUuid)
        .gte('date', startOfMonthIso)
        .lte('date', endOfMonthIso)
        .not('notes', 'is', null)

    if (error) {
        console.warn('apiGetCaseManagerNotes: query failed', error)
        return empty
    }

    const map = {}
    for (const row of data || []) {
        if (!row?.date || !row?.period) continue
        const key = `${row.date}-${row.period}`
        let parsed = []
        try {
            if (Array.isArray(row.notes)) {
                parsed = row.notes
            } else if (typeof row.notes === 'string' && row.notes.trim()) {
                const decoded = JSON.parse(row.notes)
                parsed = Array.isArray(decoded) ? decoded : []
            }
        } catch (err) {
            console.warn(
                `apiGetCaseManagerNotes: malformed notes JSON for ${key}`,
                err
            )
            parsed = []
        }
        if (parsed.length > 0) {
            map[key] = parsed
        }
    }
    return map
}
